import {useLayoutEffect} from 'react';
import {useDispatch, useStore} from 'react-redux';
import {setModalSizeSpec} from '../actions/modal';
import {MEDIUM_SCREEN_WIDTH, SMALL_SCREEN_WIDTH} from '../constants/screenConstants';
import type {RootState} from '../reducers/rootReducer';

export type CenteredModalSizeSpecStyle = Record<string, string | number>;

export type WindowSpecPayload = {
  scrollTop?: number;
  offsetTop?: number;
  windowHeight?: number;
  windowWidth?: number;
  headerHeight?: number;
  footerHeight?: number;
  scrollHeight?: number;
};

const CENTERED_WIDTH_MODAL_Y_SPACING_PX = 20;

/** Space left above/below a max-height viewport-centered dialog (matches `100vh - 32px`). */
const VIEWPORT_CENTERED_MODAL_VERTICAL_GUTTER_PX = 32;
const VIEWPORT_CENTERED_MODAL_MAX_HEIGHT_PX = 720;

/** True when content uses `left: 50%` + `translate(-50%, -50%)` viewport centering. */
export function isViewportCenteredModalContent(content: CenteredModalSizeSpecStyle | undefined): boolean {
  return Boolean(
    content &&
      content.left === '50%' &&
      typeof content.transform === 'string' &&
      content.transform.includes('translate(-50%, -50%)')
  );
}

/** True when content uses `left: 50%` + `translateX(-50%)` width centering (not full viewport center). */
export function isCenteredWidthModalContent(content: CenteredModalSizeSpecStyle | undefined): boolean {
  return Boolean(
    content &&
      content.left === '50%' &&
      typeof content.transform === 'string' &&
      content.transform.includes('translateX') &&
      !isViewportCenteredModalContent(content)
  );
}

/** Reads the last parent `GET_WINDOW_SPEC` values cached in the scroll reducer. */
export function getCachedWindowSpecPayload(state: RootState): WindowSpecPayload | undefined {
  const {scrollTop, offsetTop, windowHeight, headerHeight} = state.scroll ?? {};
  if (!windowHeight) {
    return undefined;
  }

  return {scrollTop, offsetTop, windowHeight, headerHeight};
}

/** Applies scroll-aware `top` / `maxHeight` for `translateX(-50%)` centered-width modals (FormModal, etc.). */
export function buildScrollAwareCenteredWidthContent(
  baseContent: CenteredModalSizeSpecStyle,
  payload: WindowSpecPayload = {}
): CenteredModalSizeSpecStyle {
  const windowHeight = payload.windowHeight ?? 0;
  const windowWidth = payload.windowWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 0);
  const isSmallScreen = windowHeight < SMALL_SCREEN_WIDTH || windowWidth < MEDIUM_SCREEN_WIDTH;

  // Mobile layout is handled by `App.tsx` `updateModalSize` (full-bleed iframe margins).
  // Do not override positioning here — mixing `left: 0` with `translateX(-50%)` shifts the shell off-screen.
  if (isSmallScreen) {
    return baseContent;
  }

  const calculatedTop = Math.max(0, (payload.scrollTop ?? 0) - (payload.offsetTop ?? 0));
  const headerHeight = payload.headerHeight ?? 0;

  let maxHeightPx = Math.max(0, windowHeight - headerHeight - 2 * CENTERED_WIDTH_MODAL_Y_SPACING_PX);
  if (
    payload.scrollTop != null &&
    payload.windowHeight != null &&
    payload.footerHeight != null &&
    payload.scrollHeight != null
  ) {
    if (payload.scrollTop + payload.windowHeight + payload.footerHeight >= payload.scrollHeight) {
      maxHeightPx = Math.max(
        0,
        windowHeight - headerHeight - payload.footerHeight - 2 * CENTERED_WIDTH_MODAL_Y_SPACING_PX
      );
    }
  }

  return {
    ...baseContent,
    top: `${calculatedTop + CENTERED_WIDTH_MODAL_Y_SPACING_PX}px`,
    bottom: 'auto',
    maxHeight: `${maxHeightPx}px`,
    ...(payload.scrollTop !== undefined ? {scrollTop: payload.scrollTop} : {}),
  };
}

/** Visible iframe band below the fixed DWM header (parent-page coords converted to iframe Y). */
export function computeViewportCenteredVisibleBounds(payload: WindowSpecPayload = {}): {
  visibleTop: number;
  visibleHeight: number;
} {
  const calculatedTop = Math.max(0, (payload.scrollTop ?? 0) - (payload.offsetTop ?? 0));
  const headerHeight = Math.max(0, payload.headerHeight ?? 0);
  const windowHeight = Math.max(0, payload.windowHeight ?? 0);
  return {
    visibleTop: calculatedTop + headerHeight,
    visibleHeight: Math.max(0, windowHeight - headerHeight),
  };
}

/** Vertical center of the visible iframe viewport below the DWM header. */
export function computeViewportCenteredModalTop(payload: WindowSpecPayload = {}): number {
  const {visibleTop, visibleHeight} = computeViewportCenteredVisibleBounds(payload);
  return visibleTop + visibleHeight / 2;
}

/** Pixel `maxHeight` that keeps a tall dialog inside the visible band (header + gutter). */
export function computeViewportCenteredModalMaxHeight(payload: WindowSpecPayload = {}): string | undefined {
  const windowHeight = payload.windowHeight;
  if (windowHeight == null || windowHeight <= 0) {
    return undefined;
  }

  const headerHeight = Math.max(0, payload.headerHeight ?? 0);
  const maxHeightPx = Math.min(
    VIEWPORT_CENTERED_MODAL_MAX_HEIGHT_PX,
    Math.max(0, windowHeight - headerHeight - VIEWPORT_CENTERED_MODAL_VERTICAL_GUTTER_PX)
  );
  return `${maxHeightPx}px`;
}

/** Applies scroll-aware `top` (and header-aware `maxHeight`) for viewport-centered modals. */
export function buildScrollAwareViewportCenteredContent(
  baseContent: CenteredModalSizeSpecStyle,
  payload?: WindowSpecPayload
): CenteredModalSizeSpecStyle {
  const maxHeight = computeViewportCenteredModalMaxHeight(payload);
  return {
    ...baseContent,
    position: 'absolute',
    top: `${computeViewportCenteredModalTop(payload)}px`,
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    margin: '0',
    transform: 'translate(-50%, -50%)',
    ...(maxHeight ? {maxHeight} : {}),
  };
}

/**
 * Base spec for a viewport-centered dialog. Pair with `useCenteredModalSizeSpec` — the hook
 * re-applies scroll-aware `top` after each `GET_WINDOW_SPEC` (runs after `App.tsx`'s handler).
 * Use `VIEWPORT_CENTERED_MODAL_OVERLAY_SPEC` for the matching full-viewport dimmer overlay.
 */
export function buildViewportCenteredModalContentSpec(
  maxWidth: string,
  options: {maxHeight?: string; borderRadius?: string} = {}
): CenteredModalSizeSpecStyle {
  return {
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    margin: '0',
    transform: 'translate(-50%, -50%)',
    width: `min(calc(100vw - 32px), ${maxWidth})`,
    maxWidth,
    maxHeight: options.maxHeight ?? 'min(calc(100vh - 32px), 720px)',
    height: 'fit-content',
    borderRadius: options.borderRadius ?? '1.25rem',
    padding: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 999999,
  };
}

/** Full-viewport dimmer paired with `buildViewportCenteredModalContentSpec` (content uses z-index 999999). */
export const VIEWPORT_CENTERED_MODAL_OVERLAY_SPEC: CenteredModalSizeSpecStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 999998,
};

/** True once `modalSizeSpec.content` carries a centered horizontal shell (`left: 50%` + translate). */
export function isCenteredModalSizeSpecReady(content: CenteredModalSizeSpecStyle | undefined): boolean {
  if (!content || Object.keys(content).length === 0) {
    return false;
  }

  return isCenteredWidthModalContent(content) || isViewportCenteredModalContent(content);
}

function isModalContentSpecIntact(
  current: CenteredModalSizeSpecStyle | undefined,
  expected: CenteredModalSizeSpecStyle
): boolean {
  if (!current) {
    return false;
  }

  if (isViewportCenteredModalContent(expected)) {
    return isViewportCenteredModalContent(current);
  }

  return current.left === expected.left && current.transform === expected.transform;
}

function resolveModalContentSpec(
  content: CenteredModalSizeSpecStyle,
  payload?: WindowSpecPayload
): CenteredModalSizeSpecStyle {
  if (isViewportCenteredModalContent(content)) {
    return buildScrollAwareViewportCenteredContent(content, payload);
  }

  if (isCenteredWidthModalContent(content) && payload) {
    return buildScrollAwareCenteredWidthContent(content, payload);
  }

  return content;
}

export type UseCenteredModalSizeSpecArgs = {
  /** Whether the modal is currently open; the spec is only registered while true. */
  isOpen: boolean;
  /** Unique Redux `modalType` for this modal (dispatched with `SHOW_MODAL`). */
  modalType: string;
  /**
   * `modalSizeSpec.content` for a "centered-width" dialog: `left: '50%'` + a `translateX(-50%)`
   * transform + a fixed `maxWidth` (see `App.tsx`'s `usesCenteredWidthLayout` branch).
   *
   * For viewport-centered dialogs, use `buildViewportCenteredModalContentSpec` — this hook applies
   * scroll-aware vertical centering on every `GET_WINDOW_SPEC`.
   */
  content: CenteredModalSizeSpecStyle;
  /** Optional `modalSizeSpec.overlay` override (e.g. a lighter dimmer opacity). */
  overlay?: CenteredModalSizeSpecStyle;
  /**
   * When true, only manages `modalSizeSpec` (and `GET_WINDOW_SPEC` healing). `SHOW_MODAL` /
   * `HIDE_MODAL` are owned by {@link ModalRoot} and the opener's dispatch.
   */
  skipModalVisibilityDispatch?: boolean;
};

/**
 * Registers a centered modal (`ModalWrapper` + the shared Redux `modal` slice) and keeps its
 * `content` from being clobbered by `App.tsx`'s `GET_WINDOW_SPEC` handler.
 *
 * `App.tsx` registers its handler once on mount with a stale `propModal` snapshot, so it often
 * replaces our spec with the generic iframe-margin layout. This hook listens for the same message,
 * reads live Redux state via `useStore`, and re-applies our spec on the next animation frame
 * (after `App.tsx` runs). Viewport-centered specs also get a scroll-aware `top` from the payload.
 */
export function useCenteredModalSizeSpec({
  isOpen,
  modalType,
  content,
  overlay,
  skipModalVisibilityDispatch = false,
}: UseCenteredModalSizeSpecArgs): void {
  const dispatch = useDispatch();
  const store = useStore<RootState>();

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const applySpec = (payload?: WindowSpecPayload) => {
      const effectivePayload = payload ?? getCachedWindowSpecPayload(store.getState());
      const resolvedContent = resolveModalContentSpec(content, effectivePayload);
      dispatch(setModalSizeSpec(overlay ? {content: resolvedContent, overlay} : {content: resolvedContent}));
    };

    applySpec();
    if (!skipModalVisibilityDispatch) {
      const modalProps = store.getState().modal?.modalProps ?? {};
      dispatch({type: 'SHOW_MODAL', modalType, modalProps});
    }

    const healAfterWindowSpec = (event: MessageEvent) => {
      if (event?.data?.type !== 'GET_WINDOW_SPEC') return;

      requestAnimationFrame(() => {
        const modalState = store.getState().modal;
        if (modalState?.modalType !== modalType) return;

        const payload = event.data.payload as WindowSpecPayload | undefined;

        if (isViewportCenteredModalContent(content) || isCenteredWidthModalContent(content)) {
          applySpec(payload);
          return;
        }

        const currentContent = modalState?.modalSizeSpec?.content as CenteredModalSizeSpecStyle | undefined;
        if (!isModalContentSpecIntact(currentContent, content)) {
          applySpec(payload);
        }
      });
    };

    window.addEventListener('message', healAfterWindowSpec);

    return () => {
      window.removeEventListener('message', healAfterWindowSpec);
      if (!skipModalVisibilityDispatch) {
        dispatch({type: 'HIDE_MODAL'});
      }
      dispatch(setModalSizeSpec({content: {}}));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalType]);
}
